import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { LayoutChangeEvent } from "react-native"
import { ScrollView } from "react-native-gesture-handler"

import styled from "styled-components/native"

import rawTokens from "@treefort/tokens/app"

import { useAppLinkHelpers } from "../hooks/use-app-link-helpers"
import { HeadingLevel } from "./heading"
import LinearGradient from "./linear-gradient"
import { Markdown } from "./markdown"
import Modal from "./modal"
import Spacer from "./spacer"
import Text, { getTextMaxWidthStyle, TextProps } from "./text"
import { useTokens } from "./tokens-provider"
import Touchable from "./touchable"

type BackgroundColor = keyof typeof rawTokens.colors.background

export type TextToggleProps = Omit<TextProps, "children"> & {
  type?: "expander" | "modal"
  title?: string
  onReady?: (ready: boolean) => unknown
  maxHeight: number
  backgroundColor?: BackgroundColor
  children: string
  maxAllowedHeadingLevel?: HeadingLevel
}

// This expands the tappable area so that the tiny "Read more" and "See less"
// links are comfortably within the bounds. RN's built-in hit slop feature is
// not used because more often than not the slop is clipped by the parent view.
const HIT_SLOP_STYLE = { padding: 16, margin: -16 }

// Unlocks a scroll view during body scroll locking on the web
const WEB_SCROLL_UNLOCK = { webScrollUnlock: true }

// If the text is longer than maxHeight but shorter than
// maxHeight + this allowance, then we'll show it anyway to
// avoid an awkwardly-short reveal when tapping "Read more".
const MAX_HEIGHT_ALLOWANCE = 30

const Container = styled.View<{
  $ready: boolean
  $maxWidth?: TextProps["maxWidth"]
}>`
  position: relative;
  opacity: ${(props) => (props.$ready ? 1 : 0)};
  ${(props) =>
    getTextMaxWidthStyle({
      tokens: props.theme,
      maxWidth: props.$maxWidth,
    })};
`

const HiddenTextContainer = styled.View`
  opacity: 0;
  left: 0;
  position: absolute;
  /* Set this to an extreme value to avoid causing extra body scroll in Safari
  when near the bottom of the page */
  top: -9999%;
`

const ReadMoreContainer = styled.View`
  position: absolute;
  bottom: 0;
  left: 0;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  margin: 0;
  padding: 0;
`

const ReadMoreGradient = styled(LinearGradient)`
  position: absolute;
  bottom: 10px;
  left: 0;
  height: 50px;
  width: 100%;
`

const ReadMoreGradientStart = styled.View<{ $color: string }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 10px;
  width: 100%;
  background: ${(props) => props.$color};
`

const ReadMoreText = styled(Text)`
  white-space: nowrap;
  background-color: transparent;
`

/**
 * This component displays a specified height of content along with a read
 * more button. This read more button is not displayed if the entire text fits
 * within the given height.
 *
 * There is a callback that is called once the component has calculated if the
 * text needs truncated. This is useful if you want other components on a page
 * to show their loading states until this component is done loading. The pages
 * just look a little better if everything loads together, and it's just one
 * render cycle so it isn't a big deal to wait for it.
 */
function TextToggle({
  children,
  maxHeight,
  type = "expander",
  title,
  onReady,
  backgroundColor = "primary",
  maxAllowedHeadingLevel = 1,
  ...textProps
}: TextToggleProps): React.JSX.Element | null {
  const cancelled = useRef<boolean>(undefined)
  const { tokens } = useTokens()
  const [expanded, setExpanded] = useState(false)
  const [textIsTruncated, setTextIsTruncated] = useState<boolean>()
  const ready = textIsTruncated !== undefined
  const toggleText = useCallback(() => {
    setExpanded((expanded) => !expanded)
  }, [])
  const { t } = useTranslation()

  const appLinkHelpers = useAppLinkHelpers()

  const onLayoutHiddenText = useCallback(
    (event: LayoutChangeEvent) => {
      if (!cancelled.current) {
        // On iOS layout.height can change from a whole number to a decimal
        // across renders, changing the value of renderedLines and causing an
        // infinite flicker effect if it's not rounded
        const calculatedHeight = Math.round(event.nativeEvent.layout.height)

        setTextIsTruncated(calculatedHeight > maxHeight + MAX_HEIGHT_ALLOWANCE)
      }
    },
    [maxHeight],
  )

  const parsedContent = (
    <Markdown
      maxAllowedHeadingLevel={maxAllowedHeadingLevel}
      appLinkHelpers={appLinkHelpers}
    >
      {children}
    </Markdown>
  )

  useEffect(() => {
    if (onReady) {
      onReady(ready)
    }
  }, [ready, onReady])

  useEffect(
    () => () => {
      cancelled.current = true
    },
    [],
  )

  return children ? (
    <Container $ready={ready} $maxWidth={textProps.maxWidth}>
      <HiddenTextContainer
        pointerEvents="none"
        aria-hidden
        onLayout={onLayoutHiddenText}
      >
        {/*
        This text is used to determine the number of lines the component will
        take up to see whether or not it needs truncated.
        */}
        {parsedContent}
      </HiddenTextContainer>
      {
        // you can press the text to expand but not to collapse. It also doesn't
        // need to be pressable if it doesn't need truncated
        textIsTruncated && (!expanded || type === "modal") ? (
          <Touchable
            id={null}
            onPress={toggleText}
            style={{
              ...HIT_SLOP_STYLE,
              height: maxHeight,
              overflow: "hidden",
              padding: 0,
              margin: 0,
            }}
          >
            {parsedContent}
            <ReadMoreContainer>
              <ReadMoreGradient
                fadeTowards="top"
                color={tokens.colors.background[backgroundColor]}
              />
              <ReadMoreGradientStart
                $color={tokens.colors.background[backgroundColor]}
              />
              <ReadMoreText textStyle="strong" color="highContrast">
                {t("Read more")}
              </ReadMoreText>
            </ReadMoreContainer>
          </Touchable>
        ) : textIsTruncated && expanded ? (
          <>
            {parsedContent}
            <Spacer size="xsmall" />
            <Touchable id={null} onPress={toggleText} style={HIT_SLOP_STYLE}>
              <Text textStyle="strong" color="highContrast">
                {t("Show less")}
              </Text>
            </Touchable>
          </>
        ) : (
          parsedContent
        )
      }
      {type === "modal" ? (
        <TextToggleModal
          title={title}
          open={expanded}
          onPressOutside={toggleText}
          onPressCloseButton={toggleText}
        >
          {parsedContent}
        </TextToggleModal>
      ) : null}
    </Container>
  ) : null
}

export function TextToggleModal(
  props: Parameters<typeof Modal>[0] & { children: React.ReactNode },
) {
  const { tokens } = useTokens()
  return (
    <Modal {...props} maxWidth={460}>
      <ScrollView
        dataSet={WEB_SCROLL_UNLOCK}
        contentContainerStyle={{ padding: tokens.spacing.medium }}
      >
        {props.children}
      </ScrollView>
    </Modal>
  )
}

export default TextToggle
